import { MAP_DEFAULTS } from '@app/consts';

// Calculates new LatLng coordinates offset by a pixel distance,
// considering current map zoom and projection.

export const getLatLngOffsetByPixels = (
    map: google.maps.Map,
    position: google.maps.LatLngLiteral,
    offsetXPx: number,
    offsetYPx: number,
): google.maps.LatLngLiteral | null => {
    // Get map projection to convert between screen and lat/lng coordinates
    const projection = map.getProjection();
    if (!projection) return null;

    // Convert lat/lng to projected point
    const point = projection.fromLatLngToPoint(new google.maps.LatLng(position.lat, position.lng));
    if (!point) return null;

    // Calculate scale based on current zoom level
    const scale = Math.pow(2, map.getZoom() ?? 0);

    // Apply pixel offset, adjusted for zoom
    const newPoint = new google.maps.Point(
        point.x + offsetXPx / scale,
        point.y + offsetYPx / scale,
    );

    // Convert back to lat/lng
    const newLatLng = projection.fromPointToLatLng(newPoint);

    // Return as LatLngLiteral
    return newLatLng ? { lat: newLatLng.lat(), lng: newLatLng.lng() } : null;
};

// Ensures the value is a plain LatLngLiteral
export const getLatLngLiteral = (
    position: google.maps.LatLng | google.maps.LatLngLiteral,
): google.maps.LatLngLiteral => {
    if (!position) {
        throw new Error('[Map Utils] Position parameter is required');
    }

    if ('lat' in position && 'lng' in position) {
        if (typeof position.lat === 'function' && typeof position.lng === 'function') {
            return { lat: position.lat(), lng: position.lng() };
        }

        return position as google.maps.LatLngLiteral;
    }

    throw new Error('[Map Utils] Invalid LatLng object');
};

/**
 * Calculates the map zoom level to cover the given radius in meters
 * within the viewport width in pixels.
 * Based on https://gist.github.com/kibotu/e09e48b143d4816062818a6820d03b78
 *
 * @param radiusMeters - radius around the center point in meters
 * @param viewportWidthPx - width of the map viewport in pixels
 * @param viewportHeightPx - height of the map viewport in pixels
 * @param lat - latitude of the center point in degrees
 * @returns zoom level (clamped between 0 and 21)
 */
export function calculateZoomFromRadius(
    radiusMeters: number,
    viewportWidthPx: number,
    viewportHeightPx: number,
    lat: number,
): number {
    // Base meters per pixel at zoom level 0 at the equator
    const BASE_METERS_PER_PIXEL = 156543.03392;
    // Convert latitude to radians for calculation
    const latRad = (lat * Math.PI) / 180;
    // Calculate meters per pixel needed to fit the radius in viewport
    const metersPerPixel = (radiusMeters * 2) / viewportWidthPx;
    // Calculate raw zoom level based on formula from Google Maps team
    const rawZoom = Math.log2((BASE_METERS_PER_PIXEL * Math.cos(latRad)) / metersPerPixel);
    // Slightly reduce zoom level for large areas to keep more context visible
    const shouldFloor = radiusMeters > 100_000;
    // Normalize zoom level
    const zoom = shouldFloor ? Math.floor(rawZoom) : Math.round(rawZoom);
    // Clamp zoom to allowed range
    return Math.min(Math.max(MAP_DEFAULTS.MIN_ZOOM, zoom), MAP_DEFAULTS.MAX_ZOOM);
}
